import type * as XLSX from 'xlsx';
import {
  buildHeaderToFieldMap,
  findDataSheetName,
  headerFields,
  headerKey,
  normalizeRow,
  readDataSheetRows
} from '@/lib/excel';

/**
 * Personio's labels for the columns of its employee export that the users
 * template lacks; its email, first-name, last-name, department and position
 * columns carry the users template's own labels.
 */
const PERSONIO_EXPORT_HEADERS = {
  // Only Personio labels a column so; a plain name column may be any sheet's.
  preferredName: ['Name (bevorzugt)', 'Name (preferred)', 'Nom (préféré)', 'Név (preferált)'],
  fullName: ['Name', 'Név'],
  status: ['Status', 'Statut', 'Státusz'],
  team: ['Team', 'Équipe', 'Csapat']
};

/** The field of a departments or teams template column the export does not have. */
const TEMPLATE_COLUMN = 'templateColumn';

function withPersonioHeaders(
  map: Map<string, string>,
  fields: (keyof typeof PERSONIO_EXPORT_HEADERS)[]
): Map<string, string> {
  for (const field of fields) {
    for (const header of PERSONIO_EXPORT_HEADERS[field]) map.set(headerKey(header), field);
  }
  return map;
}

/** The users header map with Personio's status column, which no template has. */
export function buildUsersUploadHeaderMap(): Map<string, string> {
  return withPersonioHeaders(buildHeaderToFieldMap('users'), ['status']);
}

/**
 * The users header map with the columns of Personio's employee export, and
 * the departments and teams template columns it lacks as {@link TEMPLATE_COLUMN}.
 */
function buildPersonioExportHeaderMap(): Map<string, string> {
  const map = withPersonioHeaders(buildHeaderToFieldMap('users'), [
    'preferredName',
    'fullName',
    'status',
    'team'
  ]);
  for (const dataArea of ['departments', 'teams'] as const) {
    for (const header of buildHeaderToFieldMap(dataArea).keys()) {
      if (!map.has(header)) map.set(header, TEMPLATE_COLUMN);
    }
  }
  return map;
}

const INACTIVE_STATUSES = new Set(['inaktiv', 'inactive', 'inactif', 'inaktív']);

/** True for Personio's "inactive" status in any language. */
export function isInactiveStatus(status: string | undefined): boolean {
  return status !== undefined && INACTIVE_STATUSES.has(status.toLowerCase());
}

/** A person of Personio's employee export; a missing cell is empty. */
export interface PersonioExportPerson {
  firstName: string;
  lastName: string;
  email: string;
  department: string;
  team: string;
  role: string;
}

/** First name before the last space, last name the last word; one word is a first name. */
function splitFullName(fullName: string): { firstName: string; lastName: string } {
  const words = fullName.split(/\s+/);
  const lastName = words.length > 1 ? words.pop()! : '';
  return { firstName: words.join(' '), lastName };
}

/**
 * The people of the main data sheet when it is Personio's employee export,
 * else undefined. An export has an email column, a name column (the full name,
 * or the first and the last name), a column only Personio has (the status or
 * the preferred name), and no column of the departments or teams template nor
 * the Stellenbeschreibung. The first and last name columns are taken as they
 * are; only a full name alone is split. Inactive people are left out, as the
 * Personio read leaves them out; the employment type is not read, as the
 * Personio read does not read it.
 */
export function readPersonioExport(workbook: XLSX.WorkBook): PersonioExportPerson[] | undefined {
  const headerToFieldMap = buildPersonioExportHeaderMap();
  const fields = headerFields(workbook, findDataSheetName(workbook), headerToFieldMap);
  const hasNameParts = fields.has('firstName') && fields.has('lastName');
  const hasName = hasNameParts || fields.has('preferredName') || fields.has('fullName');
  const hasPersonioColumn = fields.has('status') || fields.has('preferredName');
  if (
    !fields.has('email') ||
    !hasName ||
    !hasPersonioColumn ||
    fields.has(TEMPLATE_COLUMN) ||
    fields.has('roleDescription')
  ) {
    return undefined;
  }
  return readDataSheetRows(workbook, headerToFieldMap).flatMap(({ cells }) => {
    const {
      firstName = '',
      lastName = '',
      preferredName,
      fullName,
      status,
      email = '',
      department = '',
      team = '',
      role = ''
    } = normalizeRow(cells, headerToFieldMap);
    if (isInactiveStatus(status)) return [];
    const names = hasNameParts
      ? { firstName, lastName }
      : splitFullName(preferredName ?? fullName ?? '');
    if (!names.firstName && !names.lastName && !email && !department && !team && !role) return [];
    return [{ ...names, email, department, team, role }];
  });
}
